import * as api from '@gitbook/api-client';
import { assertNever } from '@gitbook/util-ts';

import type { NavigationTarget } from '../../navigation';
import type { GitSyncCurrentStatus } from './useGitSyncCurrentStatus';

/**
 * Visual presentation of a Git Sync status, shared by the space/site header button
 * and the site sidebar so every surface describes the same state identically.
 */
export type GitSyncStatusPresentation = {
    kind: 'default' | 'info' | 'success' | 'warning' | 'danger';
    label: string;
    detail?: string;
    loading: boolean;
    actionLabel?: string;
    /**
     * One-word wording for surfaces too tight for `label` and its action, such as the sidebar item.
     * Absent while there is nothing to report yet, where the surface falls back to `actionLabel`.
     */
    compactLabel?: string;
};

/**
 * Return true when the target exposes any Git Sync installation marker.
 */
export function hasGitSyncInstallation(target: api.Space | api.Site | null | undefined): boolean {
    return Boolean(target?.gitSync?.installationId);
}

/**
 * Return true when the target has any Git Sync configured, including legacy repo-only installs
 * that predate the installation id/provider/integration markers and only expose a repo name/url.
 */
export function hasAnyGitSync(target: api.Space | api.Site | null | undefined): boolean {
    return Boolean(
        hasGitSyncInstallation(target) || target?.gitSync?.repoName || target?.gitSync?.url
    );
}

/**
 * Return the navigation target that opens the site-level Git Sync dialog.
 *
 * Used both by the site-centric sidebar item and by the "managed by site" notice
 * shown when editing a space whose Git Sync is managed at the site level. Pass `status` to land on
 * the sync status view (e.g. from a failed sync) instead of the configuration.
 */
export function getSiteGitSyncSetupTarget(
    site: Pick<api.Site, 'id'>,
    organizationId: string,
    tab: 'setup' | 'status' = 'setup'
): NavigationTarget {
    return {
        screen: {
            type: 'site',
            organization: organizationId,
            site: site.id,
            sideSheet: { type: 'gitsync', tab },
        },
    };
}

/**
 * Derive the shared visual presentation for a Git Sync status from the realtime
 * status and the installation markers on the target.
 */
export function getGitSyncStatusPresentation(input: {
    gitSyncStatus: GitSyncCurrentStatus | null;
    hasInstallation: boolean;
    isPending: boolean;
}): GitSyncStatusPresentation {
    const { gitSyncStatus, hasInstallation, isPending } = input;

    if (!hasInstallation) {
        return {
            kind: 'default',
            label: 'Git Sync',
            loading: false,
            actionLabel: 'Set up',
            detail: 'Set up bidirectional sync between your content and a Git repository',
        };
    }

    if (isPending) {
        return {
            kind: 'warning',
            label: 'Git Sync',
            detail: 'Finish setting up Git Sync',
            loading: false,
            actionLabel: 'Finish setup',
            compactLabel: 'Pending',
        };
    }

    if (gitSyncStatus) {
        switch (gitSyncStatus.state) {
            case 'running':
                return {
                    kind: 'info',
                    label: 'Syncing',
                    compactLabel: 'Syncing',
                    loading: true,
                    detail:
                        gitSyncStatus.direction === api.GitSyncOperationDirection.Import
                            ? 'Importing changes from your repository'
                            : 'Exporting your recent changes to your repository',
                };
            case 'timed-out':
            case 'failure':
                return {
                    kind: 'danger',
                    label: 'Sync failed',
                    compactLabel: 'Failed',
                    loading: false,
                    detail: `The latest ${gitSyncStatus.direction === api.GitSyncOperationDirection.Import ? 'import' : 'export'} failed`,
                    actionLabel: 'View',
                };
            case 'success':
                return {
                    kind: 'success',
                    label: 'Synced',
                    compactLabel: 'Synced',
                    loading: false,
                    detail: `The latest ${gitSyncStatus.direction === api.GitSyncOperationDirection.Import ? 'import' : 'export'} succeeded`,
                };
            default:
                assertNever(gitSyncStatus);
        }
    }

    return {
        kind: 'default',
        label: 'Git Sync',
        loading: false,
    };
}
